import React, { useState } from 'react';
import styled from 'styled-components';

const faqItems = [
  {
    question: 'What is htraction and how does it help founders?',
    answer:
      'htraction is a data intelligence platform that helps founders apply and evaluated by thesis aligned investors. It makes fundraising faster, fairer and data driven'
  },
  {
    question: 'What makes htraction different from other startup or investor platforms?',
    answer:
      'Unlike basic listing or deal flow tools, htraction quantifies trust. Its algorithm assesses, benchmarks risk and compliance, and pairs them with thesis aligned investors.'
  },
  {
    question: 'Why do investors trust htraction?',
    answer:
      'htraction de-risks early-stage investing by working as a intelligence layer, surfacing early risks signals, enabling thesis-aligned deal flow, and accelerating diligence.'
  },
  {
    question: 'Are startups charged to use the platform or its embedded services?',
    answer:
      'Account creation, startup profiling, and access to thesis aligned investor matches are free. A nominal fee applies only when you submit an application to a specific investor.'
  },
  {
    question: 'How does htraction protect user data and ensure privacy?',
    answer:
      'End-to-end encryption, zero-trust principles, and strict access controls protect every data layer, ensuring startup and investor information remains secure, private, and consent-driven.'
  }
];

const Faq: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  const toggleIndex = (index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  return (
    <Section>
      <ContentWrapper>
        <LeftColumn>
          <Heading>
            Frequently
            <br />
            Asked
            <br />
            <span>Questions</span>
          </Heading>

          <CtaCard>
            <CtaTitle>
              Can’t find your answer here? <CtaLink>Talk to us</CtaLink>
            </CtaTitle>
            <a href="mailto:patankar@htraction.com"><CtaButton>Contact Us</CtaButton></a>
          </CtaCard>
        </LeftColumn>

        <RightColumn>
          {faqItems.map((faq, index) => {
            const isOpen = activeIndex === index;

            return (
              <FaqItem key={faq.question}>
                <QuestionButton onClick={() => toggleIndex(index)}>
                  <QuestionText>{faq.question}</QuestionText>
                  <ToggleIcon aria-hidden>{isOpen ? '×' : '+'}</ToggleIcon>
                </QuestionButton>
                {isOpen && <Answer>{faq.answer}</Answer>}
              </FaqItem>
            );
          })}
        </RightColumn>
      </ContentWrapper>
    </Section>
  );
};

const Section = styled.section`
  padding: 90px 60px 0px 60px;
  background-color: #fff;
  @media (max-width: 768px) {
    padding: 80px 24px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  display: flex;
  gap: 100px;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 48px;
  }
`;

const LeftColumn = styled.div`
  flex: 0.95;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Heading = styled.h2`
  font-size: clamp(40px, 4vw, 56px);
  font-weight: 700;
  line-height: 1.05;
  color: #111c2b;
  margin: 0;
  span {
    background: linear-gradient(90deg, #FF5733, #FF8C00);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const RightColumn = styled.div`
  flex: 1.3;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FaqItem = styled.div`
  border-radius: 28px;
  padding: 28px 32px;
  background: #fff;
  box-shadow: 0 25px 60px rgba(9, 20, 65, 0.01);
  border: 1px solid rgba(15, 27, 45, 0.06);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  &:hover {
    border-color: #ff5924;
    box-shadow: 0 25px 60px rgba(255, 89, 36, 0.15);
    transform: translateY(-4px);
  }
`;

const QuestionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
`;

const ToggleIcon = styled.span`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 600;
  line-height: 1;
  color: #ff5924;
  
`;

const QuestionText = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #0f1b2d;
`;

const Answer = styled.p`
  margin: 18px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(15, 27, 45, 0.8);
`;

const Badge = styled.span`
  align-self: flex-start;
  padding: 8px 18px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: rgba(38, 180, 255, 0.15);
  color: #0f6eb5;
`;

const CtaCard = styled.div`
  margin-top: auto;
  padding: 28px;
  border-radius: 24px;
  background: #ffffff;
  box-shadow: 0 20px 45px rgba(9, 20, 65, 0.01);
  border: 1px solid rgba(15, 27, 45, 0.05);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CtaTitle = styled.p`
  font-size: 20px;
  font-weight: 600;
  color: #0f1b2d;
  margin: 0;
  line-height: 1.4;
`;

const CtaLink = styled.span`
  color: #ff5924;
  cursor: pointer;
`;

const CtaButton = styled.button`
  align-self: flex-start;
  border: none;
  border-radius: 999px;
  background:#ff5924;
  color: #FFF;
  font-weight: 600;
  padding: 12px 28px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;


`;

export default Faq;
